import IRegistrationForm from "@shared/entities/Backend/IRegistrationForm";
import IOrder from "@shared/entities/RegisterEvent/IOrder";
import IRegisterEventCreateForm from "@shared/entities/RegisterEvent/IRegisterEventCreateForm";
import IRegisterEventUpdateForm from "@shared/entities/RegisterEvent/IRegisterEventUpdateForm";
import { ILevel } from "@shared/entities/RegisterEvent";
import EnumPaymentMethod from "@shared/entities/enum/EnumPaymentMethod";
import EnumPaymentType from "@shared/entities/enum/EnumPaymentType";
import EnumRegistrationUpgradeStatus from "@shared/entities/enum/EnumRegistrationUpgradeStatus";
import { IDealersDenPayload } from "@shared/entities/Dashboard/IDealersDen";
import { useHttpService } from "@shared/services";
import { useCallback } from "react";

/** 報名活動用的API (前台) */
export function useRegisterEventFrontApi() {
  const http = useHttpService();

  /**
   * 報名活動
   */
  const Register = useCallback(
    async (eventID: number, formValues: IRegisterEventCreateForm) => {
      return (
        await http.post<{
          success: boolean;
          data: { paymentType: EnumPaymentType; paymentInfo: Object };
        }>(`/registration/${eventID}`, formValues)
      ).data;
    },
    [http]
  );

  /**
   * 取得報名資料
   */
  const GetRegistration = useCallback(
    async (eventID: number) => {
      const resp = await http.get<{
        success: boolean;
        // registration為空時代表
        registration?: IRegistrationForm;
        order: IOrder;
        registrationUpgrade: {
          createdAt?: number;
          registrationUpgradeStatus: EnumRegistrationUpgradeStatus;
        };
        registrationUpgradeOrder: IOrder;
      }>(`/registration/${eventID}`);

      return resp?.data;
    },
    [http]
  );

  /**
   * 更新報名資料
   */
  const UpdateRegistration = useCallback(
    async (eventID: number, formValues: IRegisterEventUpdateForm) => {
      return (
        await http.put<{
          success: boolean;
        }>(`/registration/${eventID}`, formValues)
      ).data;
    },
    [http]
  );

  /**
   * 取消報名（不會有用？）
   */
  const CancelRegistration = useCallback(
    async (eventID: number) => {
      return (
        await http.delete<{
          success: boolean;
        }>(`/registration/${eventID}`)
      ).data;
    },
    [http]
  );

  /**
   * 重試付款
   */
  const RetryPayment = useCallback(
    async (
      eventID: number,
      couponCode: string,
      paymentMethod?: EnumPaymentMethod
    ) => {
      return (
        await http.put<{
          success: boolean;
          data: { paymentType: EnumPaymentType; paymentInfo?: Object };
        }>(`/registration/${eventID}/payment/retry`, {
          paymentMethod,
          couponCode,
        })
      ).data;
    },
    [http]
  );

  /**
   * 升級報名
   */
  const UpgradeRegistration = useCallback(
    async (
      eventID: number,
      // 2024新增：
      formValues: {
        levelID: number;
        couponCode: string;
        paymentMethod: EnumPaymentMethod;
        registrationAdditionalInfo: {}
        registrationShirtSize: string | null;
        registrationDonateAmount: number | null;
      }
    ) => {
      return (
        await http.post<{
          success: boolean;
          data: { paymentType: EnumPaymentType; paymentInfo: Object };
        }>(`/registration/${eventID}/upgrade`, formValues)
      ).data;
    },
    [http]
  );
  /**
   * 升級現場報名
   */
  const UpgradeOnSiteRegistration = useCallback(
    async (
      eventID: number,
      registrationSelectedDates: string[],
      couponCode: string,
      paymentMethod: EnumPaymentMethod
    ) => {
      return (
        await http.post<{
          success: boolean;
          data: { paymentType: EnumPaymentType; paymentInfo: Object };
        }>(`/registration/${eventID}/upgrade/dates`, {
          registrationSelectedDates,
          paymentMethod,
          couponCode,
        })
      ).data;
    },
    [http]
  );
  /**
   * 重試升級付款
   */
  const RetryUpgradeRegistration = useCallback(
    async (
      eventID: number,
      couponCode: string,
      paymentMethod: EnumPaymentMethod
    ) => {
      return (
        await http.put<{
          success: boolean;
          data: { paymentType: EnumPaymentType; paymentInfo: Object };
        }>(`/registration/${eventID}/upgrade/payment/retry`, { couponCode, paymentMethod })
      ).data;
    },
    [http]
  );

  /**
   * 取得所有等級資料
   */
  const GetLevels = useCallback(
    async (eventID: number) => {
      return (
        await http.get<{
          success: boolean;
          levels: Array<ILevel>;
          /** 
           * 2024新增：可加購的商品狀態
           * addonLimitStatus
           * key 為 limitKey，true 時可購買，false 時不可購買
           * */

          addonLimitStatus: {
            [limitKey: string]: boolean
          }
        }>(`/registration/${eventID}/levels`)
      ).data;
    },
    [http]
  );

  /** 取得報名Token資料 */
  const GetTokenData = useCallback(
    async (token: string) => {
      return (
        await http.get<{
          success: boolean;
          data: {
            eventID: number;
            levelID: number;
            level: ILevel;
            addonLimitStatus: {
              [limitKey: string]: boolean
            }
          };
        }>(`/registration/registrationToken/${token}`)
      ).data;
    },
    [http]
  );

  const UploadAvatar = useCallback(
    async (eventID: number, image: Blob) => {
      const formData = new FormData();
      formData.append("files", image);
      return (await http.post<{ success: boolean }>(`/registration/${eventID}/avatar`, formData)).data;
    },
    [http]
  );

  const DeleteAvatar = useCallback(
    async (eventID: number) => {
      return (await http.delete<{ success: boolean }>(`/registration/${eventID}/avatar`)).data;
    },
    [http]
  );

  /** Dealers Den 回覆與攤位編號（無資料時回傳 null） */
  const GetDealersDen = useCallback(
    async (eventID: number) => {
      try {
        const resp = await http.get<{ success: boolean; data: IDealersDenPayload }>(
          `/registration/${eventID}/dealersDen`
        );
        return resp?.data;
      } catch {
        return null;
      }
    },
    [http]
  );

  //220.135.175.159:3005/api/registration/{eventID}/levels
  /** 這裡導出可以用的API函數！ */
  return {
    Register,
    GetRegistration,
    UpdateRegistration,
    CancelRegistration,
    RetryPayment,
    UpgradeRegistration,
    UpgradeOnSiteRegistration,
    RetryUpgradeRegistration,
    GetLevels,
    GetTokenData,
    UploadAvatar,
    DeleteAvatar,
    GetDealersDen,
  };
}
